import Input, { IInput } from '@components/std-components/Input'
import useHierarchyItemTableContext from '@components/UI-Components/HierarchyWiseItemsTable/hooks/use-hierarchy-item-table-context'
import classNames from 'classnames'
import { useObserver } from 'mobx-react-lite'
import React, { forwardRef, memo, useReducer, useState } from 'react'
import {
  getNumberFromLocale,
  getDecimalSeparatorFromLocale
} from '@utils/get-number-form-locale'
import useFormatNumberByLocale from '@utils/useFormatNumberByLocale'
import { makeStyles } from '@material-ui/core'

export interface QtyNumberInputProps extends Omit<IInput, 'variant'> {
  id?: string
  isDecimal?: boolean
  highlightInput?: boolean
  isReset?: boolean // indicates that the field should be hard-reset to zero (workaround for temporal state mismatch)
}

const reducer = (state = { value: 0, reason: 'init' }, action) => {
  switch (action.type) {
    case 'init':
    case 'change':
    case 'focus':
      return { value: action.payload.value, reason: action.type }
    default:
      return state
  }
}

const useStyles = makeStyles(theme => ({
  numberInput: {
    width: '79%',
    height: '18px',
    font: `var(--typography-headline-bold) !important`,
    border: '1px solid rgb(206, 212, 218)',
    letterSpacing: '0.14px',
    marginTop: '5px',
    fontWeight: 600
  }
}))

const QtyNumberInput = memo(
  forwardRef<HTMLInputElement, QtyNumberInputProps>(
    (
      {
        id,
        value,
        onChange,
        onBlur,
        className,
        disabled,
        onFocus,
        onKeyPress,
        isDecimal,
        highlightInput,
        isReset,
        ...restProps
      },
      ref
    ) => {
      const classes = useStyles()
      return useObserver(function useHook() {
        const {
          store: {
            scrollPositionStore: { scrollLeft, scrollTop, actualScrollTop }
          }
        } = useHierarchyItemTableContext()

        const shouldChangeRef = React.useRef<boolean>(true)
        const [inputValue, setInputValue] = useState(value || 0)
        const formattedValue = useFormatNumberByLocale(inputValue)

        React.useEffect(() => {
          setInputValue(value)
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [value])

        const [{ value: innerValue, reason }, dispatch] = useReducer(reducer, {
          value: formattedValue,
          reason: 'init'
        })

        React.useEffect(() => {
          dispatch({ type: reason, payload: { value: formattedValue } })
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formattedValue])

        React.useEffect(() => {
          if (
            getNumberFromLocale(innerValue) !== Number(value) &&
            shouldChangeRef.current &&
            reason === 'change'
          ) {
            blurHandler?.({ target: { value: innerValue } })
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scrollLeft, scrollTop, actualScrollTop])

        const changeHandler = e => {
          const newValue = e.target.value.replace(/,/g, '')
          if (isNaN(newValue)) {
            return
          }
          shouldChangeRef.current = true
          dispatch({
            type: 'change',
            payload: { value: getNumberFromLocale(e.target.value) }
          })
        }

        const blurHandler = e => {
          if (reason === 'change' && shouldChangeRef.current) {
            if (getNumberFromLocale(e.target.value) !== Number(value)) {
              onChange?.(e)
              shouldChangeRef.current = false
            }
          }
          if (!e.target.value) {
            dispatch({ type: 'init', payload: { value: 0 } })
            return
          }
          if (getNumberFromLocale(e.target.value) === Number(value)) {
            dispatch({ type: 'init', payload: { value: formattedValue } })
            e.target.value = formattedValue
          }
          onBlur?.(e)
        }

        const onFocusHandler = e => {
          // Clear '0' for editing. Tests the form field's value rather than state to cover pending response
          if (!e.target.value) {
            dispatch({ type: 'focus', payload: { value: '' } })
          }
          onFocus?.(e)
        }

        const onKeyPressHandler = e => {
          // ignore all the characters except 0-9, from topbar and numpad
          let key
          // Handle paste
          if (e.type === 'paste') {
            key = e.clipboardData.getData('text/plain')
          } else {
            // Handle key press
            key = e.keyCode || e.which
            key = String.fromCharCode(key)
          }
          /**
           * if decimals are to be allowed, regex should be checked for decimal separator
           * note: allowed only upto 2 digits in decimals
           * the string with decimal should be used when a prop says so
           */

          const isValid = isDecimal
            ? !isNaN(getNumberFromLocale(innerValue + key)) &&
              new RegExp('^[' + getDecimalSeparatorFromLocale() + '\\d]+$').test(key)
            : !isNaN(getNumberFromLocale(innerValue + key)) && /^\d+$/.test(key)

          if (!isValid) {
            e.preventDefault()
          }

          onKeyPress?.(e)
        }

        React.useEffect(
          () => {
            if (isReset) {
              dispatch({ type: 'init', payload: { value: 0 } })
            }
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [isReset]
        )

        return (
          <Input
            {...restProps}
            inputMode={'numeric'}
            variant='outlined'
            value={innerValue}
            onChange={changeHandler}
            onBlur={blurHandler}
            className={classNames(
              classes.numberInput,
              'input--hide-arrows',
              { 'input--highlight': highlightInput },
              className
            )}
            disabled={disabled}
            min={0}
            onFocus={onFocusHandler}
            ref={ref}
            onKeyPress={onKeyPressHandler}
            onKeyUp={e => setInputValue((e.target as HTMLInputElement).value)}
            textAlign='center'
          />
        )
      })
    }
  )
)

export default QtyNumberInput
